import { useMutation } from "@apollo/client";
import { createStyles, makeStyles } from "@material-ui/core";
import illustration from "assets/icons/catalog_illustration.svg";
import { PaginationList } from "components/PaginationList";
import { EmptyTableContent } from "components/Table/NothingDisplay";
import { Title } from "components/Title";
import { TopSlogan } from "components/TopSlogan";
import { PRODUCT_TEMPLATE_HEADER, TEMPLATE_PRODUCT_URL } from "config/parcel-template";
import useText from "constants/text";
import { useAlertDispatch } from "contexts/AlertContext";
import { uploadProductsMutation } from "domains/Catalog/graphql/mutations";
import { Product } from "generated/graphql";
import { useCurrentClient } from "hooks";
import React from "react";
import ActionsGroup from "ui/ActionGroup";
import { downloadTemplate, uploadFileFunction } from "utils";
import { CatalogLoader } from "../CatalogLoader";
import { CatologItem } from "../CatologItem";

const useStyles = makeStyles(() =>
  createStyles({
    root: {
      display: "flex",
      flexDirection: "column",
      justifyContent: "left",
    },
    content: {
      marginTop: 20,
    },
    titleContent: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
    },
    list: {
      display: "flex",
      flexFlow: "wrap",
      height: "31rem",
      overflowY: "scroll",
      marginLeft: 0,
    },
  })
);

interface LeftSideCatalogProps {
  onSelectItem?: (product: Product) => void;
  handleNewArticle?: any;
  state: any;
  loading: boolean;
  refetch: any;
}

const limit = 10;

export function LeftSideCatalog({ onSelectItem, handleNewArticle, state, loading, refetch }: LeftSideCatalogProps) {
  const styles = useStyles();
  const { text } = useText();
  const [selected, setSelected] = React.useState(`-1`);
  const dispatchAlert = useAlertDispatch();
  const { client } = useCurrentClient();
  const [uploadProducts] = useMutation(uploadProductsMutation);
  const actionsList = [
    { label: text.catalog.new_article },
    { label: text.all.upload_template, type: "input", accept: ".csv" },
    { label: text.all.download_template },
  ];
  const onChange = async (e: any, funct: any) => {
    funct(true);
    e.persist();
    const { files } = e.target;
    if (files && files[0]) {
      try {
        const response = await uploadFileFunction(files[0], PRODUCT_TEMPLATE_HEADER);
        if (response.success) {
          uploadProducts({
            variables: {
              input: {
                clientId: client._id,
                products: response.data.map((p: Product) => ({ ...p, category: p.category._id })),
              },
            },
          })
            .then((res) => {
              const { statusCode } = res.data.uploadProducts;
              funct(true);
              if (statusCode === "10000") {
                refetch();
              }
              dispatchAlert({
                severity: statusCode === "10000" ? "success" : "error",
                message:
                  statusCode === "10000" ? text.all.parcel_uploaded_successfully : text.all.template_uploading_error,
              });
            })
            .catch(() => {
              funct(true);
              dispatchAlert({
                severity: "error",
                message: text.all.template_uploading_error,
              });
            });
        } else {
          funct(false);
          dispatchAlert({
            severity: "error",
            message: text.all.template_uploading_error,
          });
        }
      } catch (error) {
        funct(false);
        dispatchAlert({
          severity: "error",
          message: text.all.template_uploading_error,
        });
      }
    }
  };
  const ActionsButton = () => (
    <ActionsGroup
      buttonName="Actions"
      onChange={onChange}
      actions={actionsList.map((e) => ({ ...e, key: e.label }))}
      onClick={(key) => {
        if (key === text.catalog.new_article) {
          handleNewArticle();
        }
        if (key === text.all.download_template) {
          downloadTemplate(TEMPLATE_PRODUCT_URL, "product-tempate-sample.csv")
        }
      }}
    />
  );

  return (
    <div className={styles.root}>
      <TopSlogan
        illustration={illustration}
        buttonTitle={text.catalog.new_article}
        buttonClick={handleNewArticle}
        messageH3={text.all.choose_the_most}
        messageH1={text.all.convenient_delivery}
        CustomButton={ActionsButton}
      />
      <div className={styles.content}>
        <div className={styles.titleContent}>
          <Title title={text.catalog.my_catalog} />
          <PaginationList
            total={state?.countTotal}
            fetched={state?.products.length}
            fetch={refetch}
            per={limit}
            totalPages={state?.totalPages}
          />
        </div>
        {!loading && state?.products.length === 0 && <EmptyTableContent />}
        <div className={styles.list}>
          {loading ? (
            <CatalogLoader />
          ) : (state?.products?.map((product: Product) => (
              <CatologItem
                product={product}
                onItemClick={(product) => {
                  setSelected(product._id);
                  onSelectItem(product);
                }}
                key={product?._id}
                selected={selected === `${product?._id}`}
              />
            ))
          )}
        </div>
      </div>
    </div>
  );
}
